import React, { useMemo } from 'react';
import { Typography, Tag, Divider, Button, Card } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import '../styles/app-redesign.css';

const { Title, Text, Paragraph } = Typography;

const KeyboardShortcutsPage = React.memo(({ onBack, onNavigate }) => {
  const shortcuts = useMemo(() => [
    {
      category: 'Navigation',
      shortcuts: [
        { keys: 'Ctrl + K', description: 'Quick search projects', platform: 'all' },
        { keys: 'Ctrl + N', description: 'Create new project', platform: 'all' },
        { keys: 'Ctrl + U', description: 'Upload document', platform: 'all' },
        { keys: 'G + D', description: 'Go to Dashboard', platform: 'all' },
        { keys: 'G + P', description: 'Go to Projects', platform: 'all' },
        { keys: 'G + D', description: 'Go to Documents', platform: 'all' },
      ]
    },
    {
      category: 'Actions',
      shortcuts: [
        { keys: 'Ctrl + /', description: 'Show keyboard shortcuts', platform: 'all' },
        { keys: 'Escape', description: 'Close modal or cancel action', platform: 'all' },
        { keys: 'Delete', description: 'Delete selected item', platform: 'all' },
        { keys: 'Enter', description: 'Confirm action or open item', platform: 'all' },
        { keys: 'Space', description: 'Select/deselect item', platform: 'all' },
      ]
    },
    {
      category: 'View',
      shortcuts: [
        { keys: 'Ctrl + +', description: 'Zoom in (PDF viewer)', platform: 'all' },
        { keys: 'Ctrl + -', description: 'Zoom out (PDF viewer)', platform: 'all' },
        { keys: 'Ctrl + 0', description: 'Reset zoom (PDF viewer)', platform: 'all' },
        { keys: 'F', description: 'Toggle fullscreen (PDF viewer)', platform: 'all' },
      ]
    },
    {
      category: 'Mac Specific',
      shortcuts: [
        { keys: 'Cmd + K', description: 'Quick search projects', platform: 'mac' },
        { keys: 'Cmd + N', description: 'Create new project', platform: 'mac' },
        { keys: 'Cmd + U', description: 'Upload document', platform: 'mac' },
        { keys: 'Cmd + /', description: 'Show keyboard shortcuts', platform: 'mac' },
        { keys: 'Cmd + ,', description: 'Open settings', platform: 'mac' },
      ]
    }
  ], []);

  const renderCategory = useMemo(() => (category) => {
    const categoryShortcuts = category.shortcuts.filter(s => 
      s.platform === 'all' || (s.platform === 'mac' && navigator.platform.toUpperCase().indexOf('MAC') >= 0)
    );

    if (categoryShortcuts.length === 0) return null;

    return (
      <div key={category.category} style={{ marginBottom: '32px' }}>
        <Title level={3} style={{ marginBottom: '16px', color: '#1A1D23' }}>
          {category.category}
        </Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {categoryShortcuts.map((shortcut) => (
            <div 
              key={shortcut.keys} 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#fafafa',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#fafafa';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <Tag 
                style={{ 
                  fontFamily: 'JetBrains Mono, monospace', 
                  fontSize: '13px',
                  background: 'linear-gradient(135deg, #FF6B35 0%, #E85A28 100%)',
                  border: 'none',
                  color: 'white',
                  padding: '4px 8px',
                  fontWeight: 600
                }}
              >
                {shortcut.keys}
              </Tag>
              <Text style={{ fontSize: '14px', flex: 1, marginLeft: '16px', color: '#1A1D23' }}>
                {shortcut.description}
              </Text>
            </div>
          ))}
        </div>
      </div>
    );
  }, []);

  return (
    <div className="app-content-redesign">
      {/* Header */}
      <div className="dashboard-header-redesign">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            type="text"
            style={{ 
              fontSize: '16px',
              color: '#FF6B35',
              fontWeight: 600
            }}
          >
            Back
          </Button>
          <div>
            <h1 className="dashboard-title">Keyboard Shortcuts</h1>
            <p className="dashboard-subtitle">
              Speed up your workflow with these keyboard shortcuts
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 'none', textAlign: 'left' }}>
        <div style={{ 
          background: 'linear-gradient(135deg, #FFF5F0 0%, #FFFFFF 100%)',
          padding: '24px',
          borderRadius: '8px',
          marginBottom: '32px',
          border: '1px solid #FFE5D9'
        }}>
          <Title level={4} style={{ margin: 0, marginBottom: '12px', color: '#FF6B35' }}>
            Quick Start
          </Title>
          <Paragraph style={{ margin: 0, color: '#6B7280' }}>
            Press <Tag style={{ fontFamily: 'JetBrains Mono, monospace', background: '#FF6B35', color: 'white', border: 'none' }}>Ctrl + /</Tag> (or <Tag style={{ fontFamily: 'JetBrains Mono, monospace', background: '#FF6B35', color: 'white', border: 'none' }}>Cmd + /</Tag> on Mac) to show this shortcuts page anytime.
          </Paragraph>
        </div>

        {shortcuts.map(renderCategory)}

        <Divider />

        <div style={{ 
          background: '#f8f9fa',
          padding: '24px',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <Title level={4} style={{ margin: 0, marginBottom: '16px', color: '#1A1D23' }}>
            Pro Tips
          </Title>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#6B7280', fontSize: '14px', lineHeight: '1.6' }}>
            <li style={{ marginBottom: '8px' }}>Use <Tag style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: '#e9ecef', color: '#495057', border: 'none', padding: '2px 6px' }}>Ctrl + K</Tag> to quickly search for projects without touching the mouse</li>
            <li style={{ marginBottom: '8px' }}>Press <Tag style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: '#e9ecef', color: '#495057', border: 'none', padding: '2px 6px' }}>Escape</Tag> to quickly close any modal or dialog</li>
            <li style={{ marginBottom: '8px' }}>Combine shortcuts for even faster navigation (e.g., <Tag style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: '#e9ecef', color: '#495057', border: 'none', padding: '2px 6px' }}>Ctrl + K</Tag>, then type, then <Tag style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px', background: '#e9ecef', color: '#495057', border: 'none', padding: '2px 6px' }}>Enter</Tag>)</li>
            <li>Most shortcuts work throughout the application, not just on specific pages</li>
          </ul>
        </div>

        <Card
          title="Need More Help?"
          variant="borderless"
          style={{
            marginTop: '48px',
            maxWidth: '400px'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Paragraph style={{ color: '#6B7280', marginBottom: '16px' }}>
              Read the full product documentation — every feature, workflow,
              and estimator best practice in one place.
            </Paragraph>
            <Button
              type="primary"
              href="https://getcivily.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: '#3B82F6',
                border: 'none',
                width: '100%'
              }}
            >
              View Documentation
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
});

KeyboardShortcutsPage.displayName = 'KeyboardShortcutsPage';

export default KeyboardShortcutsPage;
